// src/pages/GuestBillingPage.jsx
import React, { useEffect, useMemo, useState } from "react";
import axiosInstance from "../api/axiosInstance";
import { useAuth } from "../context/AuthContext";
import Breadcrumb from "../components/Breadcrumb/Breadcrumb";
import CheckOutInvoice from "../components/CheckOutInvoice"; // reused as a generic bill preview
import EditGuestBillModal from "./EditGuestBillModal";

import {
  Card,
  Row,
  Col,
  Space,
  Typography,
  Input,
  Button,
  Tag,
  Divider,
  Descriptions,
  Form,
  InputNumber,
  Table,
  Alert,
  message,
  notification,
  Statistic,
  Tooltip,
  Empty,
  Modal,             // <- added
} from "antd";
import {
  SearchOutlined,
  ReloadOutlined,
  UserOutlined,
  HomeOutlined,
  PlusOutlined,
  SaveOutlined,
  EditOutlined,
  FileTextOutlined,
  CreditCardOutlined,
  InfoCircleOutlined, // used for the Info button & modal title
  DollarOutlined,
} from "@ant-design/icons";

const { Text } = Typography;

/* ---------- helpers ---------- */
const asNum = (v) => Number(v ?? 0);
const fmtMoney = (n) =>
  new Intl.NumberFormat(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(asNum(n));

/* ============================================================
   GuestBillingPage (F&B / POS usage)
   - Search active guest by room number
   - Add ad-hoc charges and save bill
   - List + edit guest bills
   - Apply payments to bills
   - Preview/print bill (no checkout logic)
============================================================ */
const GuestBillingPage = () => {
  const { authData, logout } = useAuth();

  /* search / active guest */
  const [roomNumberSearch, setRoomNumberSearch] = useState("");
  const [activeGuest, setActiveGuest] = useState(null);
  const [guestError, setGuestError] = useState("");

  /* new charge form */
  const [form] = Form.useForm();
  const [billingItems, setBillingItems] = useState([]);

  /* save bill */
  const [savingBill, setSavingBill] = useState(false);

  /* existing bills */
  const [billings, setBillings] = useState([]);
  const [isEditBillModalOpen, setIsEditBillModalOpen] = useState(false);
  const [billToEdit, setBillToEdit] = useState(null);

  /* payments update map (billingId -> amount to add) */
  const [paymentUpdates, setPaymentUpdates] = useState({});

  /* invoice/bill preview modal */
  const [invoiceDetails, setInvoiceDetails] = useState(null);
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);

  /* info modal */
  const [showInfoModal, setShowInfoModal] = useState(false); // <- added

  /* hotel header for bill */
  const [hotelDetails, setHotelDetails] = useState({
    hotelName: "",
    address: "",
    phoneNumber: "",
  });

  /* derived totals for new charge set */
  const vatPercentage = 14; // fixed 14% VAT
  const subtotal = useMemo(
    () => billingItems.reduce((acc, it) => acc + asNum(it.amount), 0),
    [billingItems]
  );
  const vatAmount = useMemo(() => subtotal * (vatPercentage / 100), [subtotal]);
  const totalAmount = useMemo(() => subtotal + vatAmount, [subtotal, vatAmount]);
  const totalPaid = useMemo(
    () => billingItems.reduce((acc, it) => acc + asNum(it.paidAmount), 0),
    [billingItems]
  );

  /* auth guard / hotel fetch */
  useEffect(() => {
    if (!authData?.hotelId) {
      setGuestError("User authentication is missing. Please log in.");
      logout();
      return;
    }
    (async () => {
      try {
        const res = await axiosInstance.get(`/Hotel/${authData.hotelId}`);
        setHotelDetails({
          hotelName: res.data.hotelName || "Hotel Name",
          address: res.data.address || "Hotel Address",
          phoneNumber: res.data.contactNumber || "+000 000 0000",
        });
      } catch {
        // non-fatal
      }
    })();
  }, [authData?.hotelId, logout]);

  /* main: search guest by room */
  const fetchGuestByRoomNumber = async () => {
    if (!roomNumberSearch.trim()) {
      setGuestError("Please enter a room number.");
      return;
    }
    try {
      setGuestError("");
      const res = await axiosInstance.get("/GuestBillings/activeGuest", {
        params: { hotelId: authData.hotelId, roomNumber: roomNumberSearch.trim() },
      });
      const guestBooking = res.data;
      if (guestBooking) {
        setActiveGuest(guestBooking);
        await fetchBillings(guestBooking.GuestId || guestBooking.guestId);
      } else {
        setActiveGuest(null);
        setBillings([]);
        setGuestError("No active guest found for this room.");
      }
    } catch {
      setActiveGuest(null);
      setBillings([]);
      setGuestError("Failed to fetch guest details. Please try again.");
    }
  };

  const fetchBillings = async (guestId) => {
    if (!guestId) return;
    try {
      const res = await axiosInstance.get(`/GuestBillings/guest/${guestId}`, {
        params: { hotelId: authData.hotelId },
      });
      setBillings(res.data || []);
    } catch {
      // non-fatal
    }
  };

  /* reset everything */
  const resetAll = () => {
    setRoomNumberSearch("");
    setActiveGuest(null);
    setBillings([]);
    setBillingItems([]);
    form.resetFields();
    setPaymentUpdates({});
    setGuestError("");
  };

  /* add one item to the "new charges" list */
  const handleAddBillingItem = (values) => {
    const { description, amount, paidAmount } = values;
    setBillingItems((prev) => [
      ...prev,
      {
        description: description.trim(),
        amount: asNum(amount),
        paidAmount: asNum(paidAmount || 0),
      },
    ]);
    form.resetFields(["description", "amount", "paidAmount"]);
  };

  /* save guest bill (combines all staged charge rows) */
  const handleSaveGuestBill = async () => {
    if (!activeGuest) {
      message.error("No active guest selected.");
      return;
    }
    if (billingItems.length === 0) {
      message.error("Please add at least one billing item.");
      return;
    }

    const combinedDescription = billingItems.map((it) => it.description).join(", ");
    const combinedPaid = billingItems.reduce((acc, it) => acc + asNum(it.paidAmount), 0);

    const payload = {
      guestId: activeGuest.GuestId || activeGuest.guestId,
      hotelId: authData.hotelId,
      RoomNumber: activeGuest.roomNumber,
      BookingNo: activeGuest.bookingNo || "DEFAULT_VALUE",
      BookingId: activeGuest.bookingId || 0,
      description: combinedDescription,
      amount: totalAmount,
      paidAmount: combinedPaid,
      status: combinedPaid >= totalAmount ? "Paid" : "Pending",
      billedBy: `${authData.firstName} ${authData.lastName}`,
    };

    try {
      setSavingBill(true);
      await axiosInstance.post(`/GuestBillings/${authData.hotelId}`, payload);
      notification.success({ message: "Guest bill saved" });
      setBillingItems([]);
      await fetchBillings(activeGuest.GuestId || activeGuest.guestId);
    } catch (e) {
      notification.error({
        message: "Failed to save guest bill",
        description: e?.response?.data?.message || "Please try again.",
      });
    } finally {
      setSavingBill(false);
    }
  };

  /* open edit modal for one billing row */
  const handleEditBill = (bill) => {
    setBillToEdit(bill);
    setIsEditBillModalOpen(true);
  };

  /* update payment inputs per bill row (stored in paymentUpdates) */
  const setPaymentFor = (billingId, value) => {
    setPaymentUpdates((prev) => ({ ...prev, [billingId]: value }));
  };

  /* apply payments only (no checkout!) */
  const applyPayments = async () => {
    if (!activeGuest) return;

    const updates = Object.entries(paymentUpdates).filter(
      ([, val]) => asNum(val) > 0
    );
    if (updates.length === 0) {
      message.info("No payment amounts entered.");
      return;
    }

    try {
      await Promise.all(
        billings.map((b) => {
          const add = asNum(paymentUpdates[b.billingId]);
          if (add > 0) {
            const newPaid = asNum(b.PaidAmount ?? b.paidAmount ?? 0) + add;
            return axiosInstance.put(`/GuestBillings/${b.billingId}`, {
              GuestId: b.GuestId,
              HotelId: authData.hotelId,
              RoomNumber: b.RoomNumber || activeGuest.roomNumber,
              BookingNo: b.bookingNo || activeGuest.bookingNo,
              Description: b.description,
              Amount: b.amount,
              PaidAmount: newPaid,
              BilledBy: b.billedBy,
            });
          }
          return Promise.resolve();
        })
      );

      setPaymentUpdates({});
      await fetchBillings(activeGuest.GuestId || activeGuest.guestId);
      notification.success({ message: "Payments applied successfully." });
    } catch (e) {
      notification.error({
        message: "Failed to apply payments",
        description: e?.response?.data?.message || "Please try again.",
      });
    }
  };

  /* open bill preview (reusing CheckOutInvoice as a generic invoice view) */
  const openBillPreview = () => {
    if (!activeGuest) return;
    const mapped = {
      guestDetails: {
        name: `${activeGuest.firstName ?? ""} ${activeGuest.lastName ?? ""}`.trim() || "N/A",
        bookingNo: activeGuest.bookingNo || "N/A",
        roomNumber: activeGuest.roomNumber || "N/A",
        paymentAmount: 0,
      },
      billingRecords: billings || [],
    };
    setInvoiceDetails(mapped);
    setShowInvoiceModal(true);
  };

  /* tables */
  const newItemsColumns = [
    {
      title: "Description",
      dataIndex: "description",
      key: "desc",
      render: (v) => <Text>{v}</Text>,
    },
    {
      title: "Amount",
      dataIndex: "amount",
      key: "amt",
      align: "right",
      render: (v) => <Text>{fmtMoney(v)}</Text>,
    },
    {
      title: "Paid",
      dataIndex: "paidAmount",
      key: "paid",
      align: "right",
      render: (v) => <Text>{fmtMoney(v)}</Text>,
    },
  ];

  const billingColumns = [
    {
      title: "Billing ID",
      dataIndex: "billingId",
      width: 110,
    },
    {
      title: "Description",
      dataIndex: "description",
      ellipsis: true,
    },
    {
      title: "Amount",
      dataIndex: "amount",
      align: "right",
      render: (v) => fmtMoney(v),
      width: 120,
    },
    {
      title: "Paid",
      dataIndex: "PaidAmount",
      align: "right",
      render: (_, r) => fmtMoney(r.PaidAmount ?? r.paidAmount ?? 0),
      width: 120,
    },
    {
      title: "Owed",
      dataIndex: "amountOwed",
      align: "right",
      render: (v, r) =>
        fmtMoney(v ?? Math.max(0, asNum(r.amount) - asNum(r.PaidAmount ?? r.paidAmount))),
      width: 120,
    },
    {
      title: "Added",
      dataIndex: "createdDate",
      render: (v) => (v ? new Date(v).toLocaleString() : "-"),
      width: 180,
    },
    {
      title: "Billed By",
      dataIndex: "billedBy",
      width: 140,
      render: (v) => v || "-",
    },
    {
      title: "Pay Now",
      key: "paynow",
      width: 180,
      render: (_, r) => (
        <InputNumber
          min={0}
          style={{ width: "100%" }}
          placeholder="Add payment"
          value={paymentUpdates[r.billingId] ?? 0}
          onChange={(val) => setPaymentFor(r.billingId, asNum(val))}
          prefix={<CreditCardOutlined />}
        />
      ),
    },
    {
      title: "Actions",
      width: 120,
      render: (_, r) => (
        <Space>
          <Tooltip title="Edit bill">
            <Button icon={<EditOutlined />} onClick={() => handleEditBill(r)} />
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Breadcrumb />
      <Row gutter={[16, 16]}>
        <Col span={24}>
          <Card
            size="small"
            title={
              <Space>
                <HomeOutlined />
                <Text strong>Guest Billing (F&amp;B)</Text>
              </Space>
            }
            extra={
              <Space>
                <Input
                  allowClear
                  style={{ width: 220 }}
                  placeholder="Search by room number"
                  value={roomNumberSearch}
                  onChange={(e) => setRoomNumberSearch(e.target.value)}
                  prefix={<SearchOutlined />}
                />
                <Button type="primary" onClick={fetchGuestByRoomNumber} icon={<SearchOutlined />}>
                  Find Guest
                </Button>
                <Button onClick={resetAll} icon={<ReloadOutlined />}>
                  Reset
                </Button>
                <Tooltip title="How this page works">
                  <Button
                    type="text"
                    icon={<InfoCircleOutlined />}
                    onClick={() => setShowInfoModal(true)}
                  />
                </Tooltip>
              </Space>
            }
          >
            {!!guestError && (
              <Alert type="error" showIcon message={guestError} style={{ marginBottom: 12 }} />
            )}

            {!activeGuest ? (
              <Empty description="Search for an active guest by room number" />
            ) : (
              <>
                <Descriptions
                  size="small"
                  bordered
                  column={3}
                  labelStyle={{ width: 140 }}
                  title={
                    <Space>
                      <UserOutlined />
                      <Text strong>Active Guest</Text>
                    </Space>
                  }
                >
                  <Descriptions.Item label="Guest ID">
                    {activeGuest.GuestId || activeGuest.guestId || "-"}
                  </Descriptions.Item>
                  <Descriptions.Item label="Name">
                    {activeGuest.firstName} {activeGuest.lastName}
                  </Descriptions.Item>
                  <Descriptions.Item label="ID Number">
                    {activeGuest.idNumber || "-"}
                  </Descriptions.Item>
                  <Descriptions.Item label="Room Number">
                    {activeGuest.roomNumber || "-"}
                  </Descriptions.Item>
                  <Descriptions.Item label="Booking No">
                    {activeGuest.bookingNo || "-"}
                  </Descriptions.Item>
                  <Descriptions.Item label="Folio Status">
                    <Tag color={asNum(activeGuest.pendingAmount) > 0 ? "orange" : "green"}>
                      {asNum(activeGuest.pendingAmount) > 0 ? "Pending" : "Clear"}
                    </Tag>
                  </Descriptions.Item>
                </Descriptions>

                <Row gutter={16} style={{ marginTop: 12 }}>
                  <Col xs={24} md={8}>
                    <Card size="small">
                      <Statistic
                        title={
                          <Space>
                            <DollarOutlined />
                            <span>Room Folio Balance</span>
                          </Space>
                        }
                        value={fmtMoney(activeGuest.pendingAmount || 0)}
                      />
                    </Card>
                  </Col>
                  <Col xs={24} md={8}>
                    <Card size="small">
                      <Statistic title="Charges (staged)" value={fmtMoney(totalAmount)} />
                    </Card>
                  </Col>
                  <Col xs={24} md={8}>
                    <Space style={{ width: "100%", justifyContent: "flex-end" }}>
                      <Tooltip title="Preview bill">
                        <Button icon={<FileTextOutlined />} onClick={openBillPreview}>
                          Bill Preview
                        </Button>
                      </Tooltip>
                      <Tooltip title="Apply entered payments to selected bills">
                        <Button type="primary" icon={<CreditCardOutlined />} onClick={applyPayments}>
                          Apply Payments
                        </Button>
                      </Tooltip>
                    </Space>
                  </Col>
                </Row>

                <Divider />

                {/* Charges + Billing Records stacked (same column) */}
                <Row gutter={16}>
                  <Col span={24}>
                    {/* Enter Additional Charges */}
                    <Card
                      size="small"
                      title={
                        <Space>
                          <PlusOutlined />
                          <Text strong>Enter Additional Charges</Text>
                        </Space>
                      }
                      extra={<Tag color="blue">VAT {vatPercentage}%</Tag>}
                    >
                      <Form form={form} layout="inline" onFinish={handleAddBillingItem} style={{ rowGap: 10 }}>
                        <Form.Item
                          name="description"
                          rules={[{ required: true, message: "Item description is required" }]}
                        >
                          <Input placeholder="Food, Drinks, Laundry, Spa..." style={{ width: 240 }} />
                        </Form.Item>
                        <Form.Item
                          name="amount"
                          rules={[{ required: true, message: "Amount is required" }]}
                        >
                          <InputNumber min={0.01} step={0.01} placeholder="Amount" style={{ width: 140 }} />
                        </Form.Item>
                        <Form.Item name="paidAmount">
                          <InputNumber min={0} step={0.01} placeholder="Paid (optional)" style={{ width: 160 }} />
                        </Form.Item>
                        <Form.Item>
                          <Button type="primary" htmlType="submit" icon={<PlusOutlined />}>
                            Add
                          </Button>
                        </Form.Item>
                      </Form>

                      <Divider style={{ margin: "12px 0" }} />

                      {billingItems.length === 0 ? (
                        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No items added yet" />
                      ) : (
                        <>
                          <Table
                            size="small"
                            rowKey={(r, i) => `${r.description}-${i}`}
                            columns={newItemsColumns}
                            dataSource={billingItems}
                            pagination={false}
                          />
                          <Row justify="end" style={{ marginTop: 12 }}>
                            <Col>
                              <Space direction="vertical" size={2} style={{ textAlign: "right" }}>
                                <Text>Subtotal: {fmtMoney(subtotal)}</Text>
                                <Text>VAT ({vatPercentage}%): {fmtMoney(vatAmount)}</Text>
                                <Text strong>Total: {fmtMoney(totalAmount)}</Text>
                              </Space>
                            </Col>
                          </Row>

                          <Space style={{ marginTop: 12 }}>
                            <Button
                              icon={<SaveOutlined />}
                              type="primary"
                              onClick={handleSaveGuestBill}
                              loading={savingBill}
                            >
                              Save Guest Bill
                            </Button>
                            <Button
                              onClick={() => {
                                setBillingItems([]);
                                form.resetFields(["description", "amount", "paidAmount"]);
                              }}
                            >
                              Clear
                            </Button>
                          </Space>
                        </>
                      )}
                    </Card>

                    {/* --- Billing Records directly under Additional Charges --- */}
                    <Card
                      size="small"
                      style={{ marginTop: 16 }}
                      title={
                        <Space>
                          <InfoCircleOutlined />
                          <Text strong>Billing Records</Text>
                        </Space>
                      }
                    >
                      {billings.length === 0 ? (
                        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No billing records found" />
                      ) : (
                        <>
                          <Table
                            size="small"
                            rowKey="billingId"
                            columns={billingColumns}
                            dataSource={billings}
                            pagination={{ pageSize: 8 }}
                          />
                          <Alert
                            type="info"
                            showIcon
                            style={{ marginTop: 12 }}
                            message="Enter 'Pay Now' amounts per row and click 'Apply Payments' (top right) to post them."
                          />
                        </>
                      )}
                    </Card>
                  </Col>
                </Row>
              </>
            )}
          </Card>
        </Col>
      </Row>

      {/* Edit bill modal */}
      {isEditBillModalOpen && billToEdit && (
        <EditGuestBillModal
          isOpen={isEditBillModalOpen}
          billing={billToEdit}
          onClose={() => setIsEditBillModalOpen(false)}
          onBillUpdated={() => activeGuest && fetchBillings(activeGuest.GuestId || activeGuest.guestId)}
        />
      )}

      {/* Bill preview (reuses your component) */}
      {showInvoiceModal && invoiceDetails && (
        <CheckOutInvoice
          isOpen={showInvoiceModal}
          onClose={() => setShowInvoiceModal(false)}
          guestDetails={invoiceDetails.guestDetails}
          hotelDetails={hotelDetails}
          billingRecords={invoiceDetails.billingRecords}
        />
      )}

      {/* ---------- Info modal (how this page works) ---------- */}
      <Modal
        title={
          <Space>
            <InfoCircleOutlined />
            <Text strong>How to use Guest Billing (F&amp;B)</Text>
          </Space>
        }
        open={showInfoModal}
        onCancel={() => setShowInfoModal(false)}
        footer={[
          <Button key="close" type="primary" onClick={() => setShowInfoModal(false)}>
            Got it
          </Button>,
        ]}
        width={720}
      >
        <Space direction="vertical" size="middle" style={{ width: "100%" }}>
          <Alert
            type="info"
            showIcon
            message="This page is for Kitchen / Restaurant / Bar billing. It does not perform guest check-outs."
          />
          <div>
            <Text strong>Workflow</Text>
            <ol style={{ marginTop: 8, paddingInlineStart: 20 }}>
              <li>
                <Text>Search the guest by <strong>Room Number</strong> (top right of header).</Text>
              </li>
              <li>
                <Text>Under <strong>Enter Additional Charges</strong>, add items (Food, Drinks, etc.). VAT is auto-applied (14%).</Text>
              </li>
              <li>
                <Text>Click <strong>Save Guest Bill</strong> to post the staged items as one bill.</Text>
              </li>
              <li>
                <Text>Use <strong>Billing Records</strong> to view/edit past bills and record payments via <em>Pay Now</em> → <strong>Apply Payments</strong>.</Text>
              </li>
              <li>
                <Text>Use <strong>Bill Preview</strong> to generate a printable view for the guest.</Text>
              </li>
            </ol>
          </div>
          <div>
            <Text strong>Notes</Text>
            <ul style={{ marginTop: 8, paddingInlineStart: 20 }}>
              <li>
                <Text>“Room Folio Balance” shows the accommodation balance from the front desk. It’s read-only here.</Text>
              </li>
              <li>
                <Text>VAT is currently fixed at <strong>14%</strong>. If you need it configurable, replace the constant <code>vatPercentage</code>.</Text>
              </li>
              <li>
                <Text>Each saved bill records <em>billedBy</em> using the signed-in staff member.</Text>
              </li>
              <li>
                <Text>If a guest can’t be found, verify the room number, that they are checked-in, and hotel context matches your login.</Text>
              </li>
            </ul>
          </div>
        </Space>
      </Modal>
    </div>
  );
};

export default GuestBillingPage;
